<template>
  <el-dialog
    title="发放权益券"
    :visible.sync="innerVisible"
    width="760px"
    top="6vh"
    class="benefit-grant-dialog"
    append-to-body
    :close-on-click-modal="false"
  >
    <!-- 券信息 -->
    <div class="grant-benefit">
      <el-select
        v-model="benefitId"
        size="small"
        class="grant-select"
        placeholder="请选择要发放的权益券"
        @change="handleBenefitChange"
      >
        <el-option
          v-for="b in benefitOptions"
          :key="b.id"
          :label="b.name"
          :value="b.id"
        >
          <span>{{ b.name }}</span>
          <span class="opt-remain">剩余 {{ remainingLabel(b) }}</span>
        </el-option>
      </el-select>
      <div v-if="currentBenefit" class="grant-benefit-info">
        <el-image v-if="currentBenefit.cover" :src="currentBenefit.cover" fit="cover" class="gb-cover" />
        <div class="gb-main">
          <div class="gb-name">
            {{ currentBenefit.name }}
            <el-tag v-if="currentBenefit.category" :type="benefitCategoryTagType(currentBenefit.category)" size="mini" effect="plain">
              {{ benefitCategoryText(currentBenefit.category) }}
            </el-tag>
          </div>
          <div class="gb-meta">
            剩余可发 <b :class="{ 'gb-warn': !remainingEnough }">{{ remainingLabel(currentBenefit) }}</b>
            · {{ summaryOf(currentBenefit) }}
          </div>
        </div>
      </div>
      <div v-else class="grant-empty">选择券种后开始选择用户</div>
    </div>

    <!-- 选择用户 -->
    <div class="grant-block">
      <div class="grant-block-head">
        <span class="grant-block-title">选择领取用户</span>
      <el-input
        v-model="keyword"
        size="small"
        placeholder="搜索昵称 / 手机号"
        clearable
        prefix-icon="el-icon-search"
        style="width: 220px"
        @input="onKeywordInput"
      />
      </div>
      <el-table
        ref="userTable"
        v-loading="loading"
        :data="users"
        row-key="id"
        size="mini"
        border
        height="236"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="42" :reserve-selection="true" />
        <el-table-column label="用户" min-width="160">
          <template slot-scope="scope">
            <div class="u-cell">
              <el-image v-if="scope.row.avatar" :src="scope.row.avatar" fit="cover" class="u-avatar u-avatar--img">
                <div slot="error" class="u-avatar" :class="{ 'u-avatar--female': scope.row.gender === 2 }">
                  {{ (scope.row.nickname || '客')[0] }}
                </div>
              </el-image>
              <span v-else class="u-avatar" :class="{ 'u-avatar--female': scope.row.gender === 2 }">
                {{ (scope.row.nickname || '客')[0] }}
              </span>
              <div class="u-main">
                <span class="u-name">{{ scope.row.nickname || '未设置昵称' }}</span>
                <span class="u-id">编号 {{ scope.row.id }}</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="手机号" width="130" prop="phone" />
        <el-table-column label="已持此券" width="92" align="center">
          <template slot-scope="scope">
            <span :class="{ 'gb-warn': heldNum(scope.row.id) > 0 }">{{ heldNum(scope.row.id) }} 张</span>
          </template>
        </el-table-column>
      </el-table>
      <div class="grant-table-foot">
        <span class="grant-hint">
          已选 <b>{{ selected.length }}</b> 人
        </span>
        <el-pagination
          small
          layout="prev, pager, next"
          :page-size="pageSize"
          :current-page="userPage"
          :total="userTotal"
          @current-change="handleUserPage"
        />
      </div>
    </div>

    <!-- 发放设置 -->
    <div class="grant-block">
      <div class="grant-block-title">发放设置</div>
      <el-form label-width="96px" size="small" class="grant-form">
        <el-form-item label="每人张数">
          <el-input-number v-model="num" :min="1" :max="10" size="small" controls-position="right" style="width: 130px" />
          <span class="form-tip">默认为每人 1 张</span>
        </el-form-item>
        <el-form-item label="发放备注">
          <el-input
            v-model="remark"
            maxlength="30"
            show-word-limit
            placeholder="如：客服补偿·接驳延误"
            style="width: 320px"
          />
          <span class="form-tip">记入持券记录，便于日后追溯</span>
        </el-form-item>
      </el-form>
    </div>

    <div slot="footer" class="dialog-footer grant-footer">
      <span class="grant-total">
        将向 <b>{{ selected.length }}</b> 位用户发放 <b>{{ grantCount }}</b> 张
        <span v-if="currentBenefit && remainingEnough === false" class="gb-warn">（超出剩余可发量）</span>
      </span>
      <div>
        <el-button size="small" @click="innerVisible = false">取 消</el-button>
        <el-button type="primary" size="small" :disabled="!canSubmit" :loading="submitting" @click="handleConfirm">确认发放</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import {
  benefitCategoryText, benefitCategoryTagType, dispatchSummary
} from '@/mock/benefit'
import { getBenefitOptions, getBenefitDetail, grantBenefit } from '@/api/benefit'
import { getUserList } from '@/api/user'
import dictOptions from '@/mixins/dict-options'

export default {
  name: 'BenefitGrantDialog',
  mixins: [dictOptions],
  props: {
    visible: { type: Boolean, default: false },
    // 传券对象则预选券种（从券列表进入）；不传则弹框内自己选（从持券记录进入）。两种入口都允许换券
    benefit: { type: Object, default: null }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      benefitId: '', // 必须声明在 data 里：v-model 依赖它驱动 currentBenefit/canSubmit 重算
      optionsList: [],
      benefitDetail: null,
      keyword: '',
      userPage: 1,
      pageSize: 6,
      userTotal: 0,
      users: [],
      selected: [],
      num: 1,
      remark: '',
      keywordTimer: null,
      // 发放方式摘要是固定标识，只有里程碑条件要字典项来出中文
      milestoneOptions: [],
      benefitCategoryText,
      benefitCategoryTagType
    }
  },
  computed: {
    innerVisible: {
      get() { return this.visible },
      set(val) { this.$emit('update:visible', val) }
    },
    benefitOptions() {
      const list = this.optionsList.slice()
      if (this.benefit && !list.some(b => b.id === this.benefit.id)) {
        return [this.benefit].concat(list)
      }
      return list
    },
    currentBenefit() {
      if (!this.benefitId) return null
      if (this.benefit && this.benefit.id === this.benefitId) return this.benefit
      if (this.benefitDetail) return this.benefitDetail
      // 详情接口慢/失败时用下拉行兜底（id/name/total/received_count 够算剩余可发），
      // 不能让整个弹窗被一次详情请求锁死（确认发放永远灰着）
      return this.optionsList.find(b => b.id === this.benefitId) || null
    },
    grantCount() {
      return this.selected.length * (this.num || 1)
    },
    remainingEnough() {
      if (!this.currentBenefit) return true
      return this.grantCount <= this.remainingOf(this.currentBenefit)
    },
    canSubmit() {
      return !!this.currentBenefit && this.selected.length > 0 && this.grantCount > 0 && this.remainingEnough
    }
  },
  watch: {
    visible(val) {
      if (val) this.resetForm()
    }
  },
  created() {
    this.loadDictOptions({ dispatch_milestone: 'milestoneOptions' })
  },
  methods: {
    // 发放规则摘要（发放方式是固定标识，里程碑条件文案靠字典项）
    summaryOf(row) {
      return dispatchSummary(row, { milestone: this.milestoneOptions })
    },
    resetForm() {
      this.benefitId = this.benefit ? this.benefit.id : ''
      this.benefitDetail = null
      this.keyword = ''
      this.userPage = 1
      this.selected = []
      this.num = 1
      this.remark = ''
      this.$nextTick(() => {
        this.$refs.userTable && this.$refs.userTable.clearValidate()
        this.$refs.userTable && this.$refs.userTable.clearSelection()
      })
      this.loadOptions()
      this.loadUsers()
    },
    loadOptions() {
      getBenefitOptions().then(res => {
        if (res.code !== 200) return
        this.optionsList = res.data || []
      })
    },
    loadUsers() {
      this.loading = true
      const params = { page: this.userPage, page_size: this.pageSize }
      const kw = (this.keyword || '').trim()
      if (kw) params.keyword = kw
      if (this.benefitId) params.benefit_id = this.benefitId
      getUserList(params).then(res => {
        if (res.code !== 200) return
        this.users = res.data.list || []
        this.userTotal = res.data.total || 0
      }).finally(() => {
        this.loading = false
      })
    },
    onKeywordInput() {
      if (this.keywordTimer) clearTimeout(this.keywordTimer)
      this.keywordTimer = setTimeout(() => {
        this.userPage = 1
        this.loadUsers()
      }, 300)
    },
    handleUserPage(val) {
      this.userPage = val
      this.loadUsers()
    },
    handleBenefitChange() {
      this.benefitDetail = null
      this.userPage = 1
      // 换券后重拉用户列表（「已持此券」按新券统计）
      this.loadUsers()
      if (this.benefit && this.benefit.id === this.benefitId) return
      getBenefitDetail(this.benefitId).then(res => {
        if (res.code !== 200) return
        this.benefitDetail = res.data || null
      }).catch(() => {})
    },
    handleSelectionChange(rows) {
      this.selected = rows
    },
    heldNum(userId) {
      const row = this.users.find(u => u.id === userId)
      return row ? (row.benefit_held_count || 0) : 0
    },
    // 剩余可发（total=0 不限量）
    remainingOf(row) {
      if (!row) return 0
      return (row.total || 0) === 0 ? Infinity : (row.total || 0) - (row.received_count || 0)
    },
    remainingLabel(row) {
      const r = this.remainingOf(row)
      return r === Infinity ? '不限量' : r + ' 张'
    },
    handleConfirm() {
      const b = this.currentBenefit
      if (!b) return this.$message.warning('请先选择要发放的权益券')
      if (!this.selected.length) return this.$message.warning('请选择领取用户')
      const remain = this.remainingOf(b)
      if (this.grantCount > remain) {
        return this.$message.warning(`「${b.name}」剩余可发 ${remain} 张，本次需发放 ${this.grantCount} 张`)
      }
      this.$confirm(
        `确认向 ${this.selected.length} 位用户发放 ${this.grantCount} 张「${b.name}」？发放后券立即进入对方账户，不可撤回。`,
        '发放确认',
        { type: 'warning', confirmButtonText: '确认发放' }
      ).then(() => {
        this.submitting = true
        grantBenefit({
          benefit_id: this.benefitId,
          user_ids: this.selected.map(u => u.id),
          num: this.num,
          remark: (this.remark || '').trim()
        }).then(res => {
          if (res.code !== 200) return
          const count = (res.data && res.data.count) || 0
          this.$message.success(`已发放 ${count} 张，可在「持券记录」查看`)
          this.$emit('granted')
          this.innerVisible = false
        }).finally(() => {
          this.submitting = false
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.benefit-grant-dialog >>> .el-dialog__body {
  padding: 14px 30px 6px;
}
/* 券信息 */
.grant-benefit {
  padding: 12px 14px;
  border-radius: 8px;
  background: #faf7f1;
  border: 1px solid #f0ebe2;
}
.grant-select {
  width: 340px;
}
.opt-remain {
  float: right;
  margin-left: 16px;
  font-size: 12px;
  color: #a8abb2;
}
.grant-benefit-info {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e6dccd;
}
.gb-cover {
  flex: 0 0 76px;
  width: 76px;
  height: 50px;
  border-radius: 6px;
  overflow: hidden;
  background: #ece3d6;
}
.gb-main {
  min-width: 0;
}
.gb-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13.5px;
  font-weight: 600;
  color: #303133;
}
.gb-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}
.gb-warn {
  color: #d9534f;
}
.grant-empty {
  margin-top: 10px;
  font-size: 12px;
  color: #a8abb2;
}
/* 区块 */
.grant-block {
  margin-top: 14px;
}
.grant-block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.grant-block-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  padding-left: 9px;
  border-left: 3px solid var(--primary);
}
.grant-table-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
}
.grant-hint {
  font-size: 12px;
  color: #909399;
}
.grant-hint b {
  color: var(--primary);
}
.u-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
.u-avatar {
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #6b5a45;
  color: #fff;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.u-avatar--female {
  background: #a4686b;
}
/* 有头像：el-image 圆框，内图吃满 */
.u-avatar--img ::v-deep .el-image__inner {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.u-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}
.u-name {
  font-size: 12.5px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.u-id {
  font-size: 11px;
  color: #a8abb2;
}
/* 表单 */
.grant-form >>> .el-form-item {
  margin-bottom: 10px;
}
.grant-form >>> .el-form-item:last-child {
  margin-bottom: 0;
}
.form-tip {
  margin-left: 10px;
  font-size: 12px;
  color: #a8abb2;
}
/* 底栏 */
.grant-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.grant-total {
  font-size: 12.5px;
  color: #606266;
}
.grant-total b {
  color: var(--primary);
  font-size: 14px;
}
</style>
